/**
 * http通信用プラグイン
 */
import _Vue from 'vue'
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import VueCookies from 'vue-cookies'
import _router from 'vue-router'
// アプリケーション定義例外のimport
import {
  ApiBadRequestAppError,
  ApiNotFoundAppError,
  ApiRequireResetPasswordAppError,
  ApiConflictAppError,
  ApiLockedAppError,
} from '@/models/AppErrorClasses'

/**
 * ルーティング書き換え用インターフェース定義
 */
interface RouterInterface {
  /** 遷移先の設定 */
  push(path: string): void
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export default {
  /**
   * plugin登録時に実行される処理
   *
   * @param Vue Vueのコンストラクタ
   * @param router ルータ
   */
  install(Vue: typeof _Vue, router: typeof _router): void {
    // ログインや認証不要な場合のAPI
    const http = this.initAxios()
    // 認証情報付きのAPI
    const httpWithToken = this.initAxios()

    httpWithToken.interceptors.request.use((request: AxiosRequestConfig) => {
      // 認証情報の取得
      const auth = (VueCookies as any).get('auth')
      if (auth && auth.accessToken != request.headers['access-token']) {
        request.headers['access-token'] = auth.accessToken
        request.headers.client = auth.client
        request.headers.uid = auth.uid
      }
      return request
    })

    // 認証後のWeb APIでリダイレクトや共通して処理したい場合はこちらに記述を追加
    httpWithToken.interceptors.response.use(
      (reponse: any) => reponse,
      async (error: any) => {
        if (!error.response) {
          return alert('接続障害が発生しております。しばらくしてから画面を再読み込みしてください。')
        }

        // router.push等によりローディング処理が残っているケースがあるため、画面遷移時は終了処理を実行
        Vue.prototype.$loading.complete()

        // responseTypeがblobの時にerror.response.dataをjsonに書き換える
        await this.onBlobHandler(error).catch(() => {
          (router as unknown as RouterInterface).push('/error/500')
        })

        if (error.response.status === 401) {
          // パスワード再設定要求エラーかを判定する
          if (error.response.data.requireResetPassword) {
            return Promise.reject(new ApiRequireResetPasswordAppError())
          }
          // 該当しない場合は通常の401エラー
          (router as unknown as RouterInterface).push('/error/401')
        } else if (error.response.status === 400) {
          return Promise.reject(new ApiBadRequestAppError())
        } else if (error.response.status === 404) {
          // アプリケーション定義例外を投げる(任意でキャッチ出来るように)
          return Promise.reject(new ApiNotFoundAppError())
        } else if (error.response.status === 409) {
          // アプリケーション定義例外を投げる(任意でキャッチ出来るように)
          return Promise.reject(new ApiConflictAppError())
        } else if (error.response.status === 423) {
          // アプリケーション定義例外を投げる(任意でキャッチ出来るように)
          return Promise.reject(new ApiLockedAppError())
        } else if (error.response.status === 500) {
          (router as unknown as RouterInterface).push('/error/500')
        }
        return Promise.reject(error)
      }
    )

    Vue.prototype.$http = http
    Vue.prototype.$httpWithToken = httpWithToken
  },

  /**
   * レスポンスがblob系式の場合のハンドラ
   *
   * @param error エラー情報
   */
  async onBlobHandler(error: any): Promise<any> {
    if (error.request.responseType === 'blob' && error.response.data instanceof Blob) {
      const text = await new Promise((resolve, reject) => {
        const fileReader = new FileReader()

        fileReader.onerror = (): void => {
          fileReader.abort()
          reject()
        }
        fileReader.onload = (): void => {
          resolve(fileReader.result)
        }

        fileReader.readAsText(error.response.data)
      })
      // @ts-ignore
      error.response.data = JSON.parse(text)
    }
    return
  },

  /**
   * axiosの初期設定
   *
   * @returns axiosのインスタンス
   */
  initAxios(): AxiosInstance {
    return axios.create({
      baseURL: process.env.VUE_APP_API_HOST,
      timeout: 180000,
      headers: {
        'Content-Type': 'application/json',
        'X-Language': 'ja',
      },
    })
  },
}

declare module 'vue/types/vue' {
  /**
   * 呼び出し時のインターフェース定義
   */
  interface Vue {
    /** 認証なしでの通信 */
    $http: AxiosInstance
    /** 認証情報付きでの通信 */
    $httpWithToken: AxiosInstance
  }
}
